'use client';

export const dynamic = 'force-dynamic';

import { useState, useRef } from 'react';
import Link from 'next/link';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Loader2, ArrowRight, Mail, Lock, Shield, Eye, EyeOff } from 'lucide-react';
import Image from "next/image";
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import { TurnstileWidget, isCaptchaConfigured, type TurnstileInstance } from '@/components/TurnstileWidget';

interface SignupFormData {
  email: string;
  password: string;
}

export default function SignupPage() {
  const [formData, setFormData] = useState<SignupFormData>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileInstance>(null);
  const router = useRouter();

  const handleInputChange = (field: keyof SignupFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!formData.email || !formData.password) {
      return 'Please fill in all required fields';
    }

    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters long';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/vendor-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          captchaToken: captchaToken ?? undefined
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Check if it's a "user already exists" error
        if (result.error && (
          result.error.includes('User already registered') ||
          result.error.includes('already registered') ||
          result.error.includes('already exists') ||
          result.error.includes('Please login instead') ||
          (result.error.toLowerCase().includes('email') && result.error.toLowerCase().includes('taken'))
        )) {
          toast.error('An account with this email already exists. Redirecting to login...');
          setTimeout(() => {
            router.push(`/login?email=${encodeURIComponent(formData.email)}&message=${encodeURIComponent('Please login to your existing account')}`);
          }, 2000);
          return;
        }
        throw new Error(result.error || 'Failed to create account');
      }

      toast.success('Account created successfully! Please check your email for the verification code.');
      router.push(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create account');
    } finally {
      // Turnstile tokens are single-use — get a fresh one for any retry
      setCaptchaToken(null);
      captchaRef.current?.reset();
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Full-screen Background Image */}
      <div className="absolute inset-0">
        <Image
          src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/bg-1.png"
          alt="Catering background"
          width={1920}
          height={1080}
          className="h-full w-full object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black" />
      </div>

      {/* Centered Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          {/* Logo */}
          <div className="flex justify-center">
            <Link href="/" className="flex items-center gap-2 font-medium">
              <Image 
                src="https://rgvfkcfnfbtqyjlrvynz.supabase.co/storage/v1/object/public/vendor-assets/vendor-assets/white-full.png" 
                alt="MenuMate Logo" 
                width={150} 
                height={50} 
                className="h-auto w-auto"
                priority 
              />
            </Link>
          </div>



          {/* Form Card */}
          <Card className="rounded-2xl shadow-2xl">
            <CardContent className="px-9 pb-9">
              
              {/* Header */}
              <div className="space-y-2 text-left py-6 mb-4">
                <h1 className="text-2xl font-semibold text-black">Start your free trial</h1>
                <p className="text-black/60">
                Get 7 days free, grow your business
                </p>
              </div>

              <Separator className='mb-8' />
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-destructive/15 text-destructive p-3 rounded-md text-sm">
                    {error}
                  </div>
                )}


                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    autoComplete="username"
                    required
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a secure password"
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      autoComplete="new-password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>


                <TurnstileWidget ref={captchaRef} onToken={setCaptchaToken} />

                <Button
                  type="submit"
                  className="w-full"
                  variant="green"
                  disabled={isLoading || (isCaptchaConfigured && !captchaToken)}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Account...
                    </span>
                  ) : (
                    <span className="flex font-normal items-center justify-center">
                      Create Menumate account
                    </span>
                  )}
                </Button>

                {/* OR Divider */}
                <div className="relative mx-10">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-white px-2 text-gray-500">or</span>
                  </div>
                </div>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col items-center space-y-4">

              <div className="flex items-center gap-2 mb-9">
                <p className="text-sm text-gray-900">
                  Already have a MenuMate account?{' '}
                  <Link href="/login" className="text-green-600 hover:text-green-700 font-medium">
                    <span className="inline-flex items-center">Log in <ArrowRight className="ml-2 h-4 w-4" /></span> 
                  </Link>
                </p>
              </div>

              <div className="text-left">
                <p className="text-xs text-muted-foreground">
                  By proceeding, you agree to the{' '}
                  <Link href="/terms-and-conditions" className="text-green-600 hover:text-green-700 font-medium">
                    Terms and Conditions
                  </Link>
                  {' '}and{' '}
                  <Link href="/privacy-policy" className="text-green-600 hover:text-green-700 font-medium">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </CardFooter>
          </Card>

          {/* Trust Indicators */}
          {/* <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
              <div className="text-lg font-semibold text-white">30 Days</div>
              <div className="text-xs text-white/70">Free Trial</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
              <div className="text-lg font-semibold text-white">24/7</div>
              <div className="text-xs text-white/70">Support</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
              <div className="text-lg font-semibold text-white">100%</div>
              <div className="text-xs text-white/70">Secure</div>
            </div>
          </div> */}

          <div className="text-center">
            <p className="text-sm text-white/80 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              Trusted by caterers across India
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}